import { PlanByPeriod } from "./PlanByPeriod";
import { DateTime } from "luxon";
import { padEnd, padStart } from "lodash";
import { IllegalStateError, notUndef } from "shared-ts-utils-core";

export class PlanByPeriods extends Array<PlanByPeriod> {
  constructor(arg: PlanByPeriod[] | number) {
    if (Array.isArray(arg)) {
      super(...arg);
    } else {
      super(arg as number);
    }
  }

  static empty(): PlanByPeriods {
    return empty;
  }

  firstPlanOrUndef(): PlanByPeriod | undefined {
    return this.length > 0 ? this[0] : undefined;
  }
  lastPlanOrUndef(): PlanByPeriod | undefined {
    return this.length > 0 ? this[this.length - 1] : undefined;
  }
  firstPlan(): PlanByPeriod {
    if (this.length === 0) {
      throw new IllegalStateError("No plans available");
    }
    return this[0];
  }

  lastPlan(): PlanByPeriod {
    if (this.length === 0) {
      throw new IllegalStateError("No plans available");
    }
    return this[this.length - 1];
  }

  start(): DateTime<true> {
    return this[0].time;
  }

  end(): DateTime<true> {
    return this[this.length - 1].time;
  }

  calculatePlan(): PlanByPeriods {
    const firstPlan = this.firstPlan();
    let currentBacklog = firstPlan.uncoveredNeed;

    const items = this.map((it) => {
      const newEntry = it.calculatePlan(currentBacklog);
      currentBacklog = Math.abs(newEntry.uncoveredNeed);
      return newEntry;
    });
    return new PlanByPeriods(items);
  }

  redistribute(maxBackSteps = 6): PlanByPeriods {
    const items: Array<PlanByPeriod> = [];
    this.forEach((it, index) => {
      items.push(it);
      if (it.uncoveredNeed < 0) {
        let remaining = Math.abs(it.uncoveredNeed);
        let backStep = 1;
        let increasedPlan = 0;
        while (
          backStep <= maxBackSteps &&
          remaining > 0 &&
          index - backStep >= 0
        ) {
          const currItem = items[index - backStep];
          if (currItem.availableCapacity > 0) {
            const increasePlan = Math.min(
              currItem.availableCapacity,
              remaining
            );
            items[index - backStep] = currItem.increasePlan(increasePlan);
            remaining -= increasePlan;
            increasedPlan += increasePlan;
          }
          backStep++;
        }
        for (let i = index; i < items.length; i++) {
          items[i] = items[i].reduceRemaining(increasedPlan);
        }
      }
    });
    return new PlanByPeriods(items);
  }

  filterByYear(year: number): PlanByPeriods {
    const filtered = this.filter((it) => {
      return it.time.year === year;
    });
    return new PlanByPeriods(filtered);
  }

  filterBetween(from: DateTime<true>, end: DateTime<true>): PlanByPeriods {
    const filtered = this.filter((it) => {
      return it.time >= from && it.time < end;
    });
    return new PlanByPeriods(filtered);
  }

  aggregateBy(timeUnit: "year"): PlanByPeriods {
    const result: Array<PlanByPeriod> = [];
    if (this.length === 0) {
      return new PlanByPeriods(result);
    }
    let previousTimeUnitValue = this[0].time.get(timeUnit);
    let accCapacity = 0;
    let accNeed = 0;
    let accPlan = 0;
    let accAvailableCapacity = 0;
    let yearCount = 1;
    let previousYearLastPlan: PlanByPeriod | undefined = undefined;
    this.forEach((it, index) => {
      const previousPlan = index > 0 ? this[index - 1] : undefined;
      const currentTimeUnitValue = it.time.get(timeUnit);
      if (currentTimeUnitValue > previousTimeUnitValue) {
        const year = currentTimeUnitValue;
        const backlog =
          yearCount > 1
            ? notUndef(previousYearLastPlan, (it) =>
                Math.abs(it?.uncoveredNeed)
              )
            : undefined;
        const remainingNeed = notUndef(previousPlan, (it) => it.uncoveredNeed);
        result.push(
          new PlanByPeriod({
            timeUnit: timeUnit,
            time: DateTime.local(previousTimeUnitValue) as DateTime<true>,
            backlog: backlog,
            need: accNeed,
            capacity: accCapacity,
            availableCapacity: accAvailableCapacity,
            plan: accPlan,
            uncoveredNeed: remainingNeed,
          })
        );
        previousTimeUnitValue = year;
        previousYearLastPlan = previousPlan;
        accNeed = 0;
        accCapacity = 0;
        accPlan = 0;
        accAvailableCapacity = 0;
        yearCount++;
      } else if (currentTimeUnitValue === previousTimeUnitValue) {
        accNeed += it.need;
        accCapacity += (it.capacity ?? 0);
        accPlan += it.plan;
        accAvailableCapacity += it.availableCapacity;
      }
    });
    return new PlanByPeriods(result);
  }

  toString(): string {
    let str =
      "| timeUnit | time    | capacity   | backlog    | need       | plan       | availableCapacity | remainingNeed |\n";
    this.forEach((it) => {
      str += `| ${padStart(it.timeUnit, 8, " ")} `;

      const time =
        it.timeUnit === "year"
          ? it.time.year.toString()
          : `${it.time.year}:${padStart(it.time.month.toString(), 2, "0")}`;

      str += `| ${padEnd(time, 7, " ")} `;
      str += `| ${padStart(it.capacity?.toString(), 10, " ")} `;
      str += `| ${padStart(it.backlog.toString(), 10, " ")} `;
      str += `| ${padStart(it.need.toString(), 10, " ")} `;
      str += `| ${padStart(it.plan.toString(), 10, " ")} `;
      str += `| ${padStart(it.availableCapacity.toString(), 17, " ")} `;
      str += `| ${padStart(it.uncoveredNeed.toString(), 13, " ")} `;
      str += "|\n";
    });
    return str;
  }
}

const empty = new PlanByPeriods([]);
